import { useState, useEffect, useCallback } from 'react'
import apiService from '@/services/apiService'

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
  const rawData = window.atob(base64)
  const outputArray = new Uint8Array(rawData.length)
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i)
  }
  return outputArray
}

export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  )
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    checkSubscription()
  }, [])

  const checkSubscription = async () => {
    try {
      if (!('serviceWorker' in navigator)) return
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      setIsSubscribed(!!subscription)
    } catch {
      console.error('Failed to check push subscription')
    }
  }

  const subscribe = useCallback(async () => {
    setIsLoading(true)
    try {
      // Request notification permission
      const perm = await Notification.requestPermission()
      setPermission(perm)

      if (perm !== 'granted') {
        setIsLoading(false)
        return false
      }

      // Get VAPID public key from backend
      const vapidRes = await apiService.get<{ success: boolean; data: { publicKey: string } }>('/push/vapid-public-key')
      const vapidPublicKey = (vapidRes as { success: boolean; data: { publicKey: string } }).data.publicKey

      if (!vapidPublicKey) {
        console.error('VAPID public key is empty')
        setIsLoading(false)
        return false
      }

      // Get service worker registration with timeout
      const registration = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Service worker not ready (timeout)')), 10000)
        )
      ])

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource
      })

      // Send subscription to backend
      const subJson = subscription.toJSON()
      await apiService.post('/push/subscribe', {
        endpoint: subJson.endpoint,
        keys: {
          p256dh: subJson.keys?.p256dh,
          auth: subJson.keys?.auth
        }
      })

      setIsSubscribed(true)
      setIsLoading(false)
      return true
    } catch (error) {
      console.error('Failed to subscribe to push notifications:', error)
      setIsLoading(false)
      return false
    }
  }, [])

  const unsubscribe = useCallback(async () => {
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()

      if (subscription) {
        await apiService.post('/push/unsubscribe', {
          endpoint: subscription.endpoint
        })
        await subscription.unsubscribe()
      }

      setIsSubscribed(false)
    } catch (error) {
      console.error('Failed to unsubscribe:', error)
    }
  }, [])

  return {
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    isSupported: 'serviceWorker' in navigator && 'PushManager' in window
  }
}
